export interface EquipmentTypeCatalogEntry {
  id?: string;
  equipmentTypeId?: string;
  name: string;
  nameKey: string;
  shortCode: string;
  shortCodeKey: string;
  status?: 'active' | 'inactive';
  source?: 'admin' | 'suggestion';
  sourceSuggestionId?: string | null;
}

export interface EquipmentTypeCatalogIndex {
  entries: EquipmentTypeCatalogEntry[];
  nameByKey: Map<string, EquipmentTypeCatalogEntry>;
  shortCodeByKey: Map<string, EquipmentTypeCatalogEntry>;
}

const normalizeCatalogLabel = (value: unknown): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

export const normalizeEquipmentTypeCatalogKey = (value: unknown): string =>
  normalizeCatalogLabel(value).toLocaleLowerCase('pl-PL');

export const normalizeEquipmentTypeShortCodeKey = (value: unknown): string =>
  normalizeCatalogLabel(value)
    .toLocaleUpperCase('pl-PL')
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9-]/g, '');

const sortLocale = (values: string[]): string[] =>
  [...values].sort((a, b) => a.localeCompare(b, 'pl'));

export const buildEquipmentTypeCatalogIndex = (
  entries: EquipmentTypeCatalogEntry[]
): EquipmentTypeCatalogIndex => {
  const dedupeEntryKeys = new Set<string>();
  const normalizedEntries: EquipmentTypeCatalogEntry[] = [];
  const nameByKey = new Map<string, EquipmentTypeCatalogEntry>();
  const shortCodeByKey = new Map<string, EquipmentTypeCatalogEntry>();

  entries.forEach((entry) => {
    const name = normalizeCatalogLabel(entry.name);
    const shortCode = normalizeEquipmentTypeShortCodeKey(entry.shortCode);
    if (!name || !shortCode) return;

    const nameKey = normalizeEquipmentTypeCatalogKey(entry.nameKey || name);
    const shortCodeKey = normalizeEquipmentTypeShortCodeKey(entry.shortCodeKey || shortCode);
    const dedupeKey = `${nameKey}::${shortCodeKey}`;
    if (dedupeEntryKeys.has(dedupeKey)) return;
    dedupeEntryKeys.add(dedupeKey);

    const normalizedEntry: EquipmentTypeCatalogEntry = {
      ...entry,
      name,
      nameKey,
      shortCode,
      shortCodeKey,
    };

    normalizedEntries.push(normalizedEntry);

    if (!nameByKey.has(nameKey)) {
      nameByKey.set(nameKey, normalizedEntry);
    }
    if (!shortCodeByKey.has(shortCodeKey)) {
      shortCodeByKey.set(shortCodeKey, normalizedEntry);
    }
  });

  normalizedEntries.sort((a, b) => {
    const byName = a.name.localeCompare(b.name, 'pl');
    if (byName !== 0) return byName;
    return a.shortCode.localeCompare(b.shortCode, 'pl');
  });

  return {
    entries: normalizedEntries,
    nameByKey,
    shortCodeByKey,
  };
};

export const normalizeEquipmentTypeCatalogEntries = (
  entries: EquipmentTypeCatalogEntry[]
): EquipmentTypeCatalogEntry[] => buildEquipmentTypeCatalogIndex(entries).entries;

export const sortEquipmentTypeLabels = (values: string[]): string[] => sortLocale(values);
